import React, { memo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import cn from 'classnames';

import { Icons, Tooltip } from '@components/common';

import { IRadioButtonProps } from './RadioButton.types';

import { ERadioColors } from './constants';

import Label from '../Label';

import './RadioButton.scss';

const RadioButton = <T extends FieldValues>({
	checked,
	defaultChecked,
	disabled = false,
	id,
	isLabelBefore = false,
	label,
	name,
	onChange,
	setValue,
	register,
	withUseForms = false,
	color,
	value,
	isError = false,
	tooltipContent,
	withTooltip = false,
	tooltipPlacement = 'top',
	infoText,
}: IRadioButtonProps<T>) => {
	const [isInfoHovered, setIsInfoHovered] = useState(false);
	const labelRef = useRef<HTMLLabelElement | null>(null);

	const handleLabelClick = () => {
		if (labelRef?.current) {
			labelRef.current.click();
		}
	};

	const handleChange = () => {
		if (onChange) onChange(value);
		if (setValue) setValue(name, value);
	};

	const toggleIsInfoHovered = () => {
		setIsInfoHovered(isInfoHovered => !isInfoHovered);
	};

	const paletteClasses = {
		'f5gRadioButtonRed': color === ERadioColors.red || isError,
		'f5gRadioButtonAmber': color === ERadioColors.amber,
		'f5gRadioButtonGreen': color === ERadioColors.green,
		'f5gRadioButtonOrange': color === ERadioColors.orange,
		'f5gRadioButtonDisabled': disabled,
	};

	return (
		<div className='f5gRadioButtonContainer'>
			{isLabelBefore && (
				<Label
					isFieldLabel={false}
					label={label}
					onClick={handleLabelClick}
				/>
			)}
			<Tooltip
				content={tooltipContent}
				isDisabled={!withTooltip || !tooltipContent || checked}
				placement={tooltipPlacement}
			>
				<label
					ref={labelRef}
					className={cn('f5gRadioButton', {
						...paletteClasses,
						'f5gRadioButtonDisabled': disabled,
					})}
					htmlFor={id}
				>
					{withUseForms && name && typeof value !== 'boolean'
						? (
							<input
								data-testid='radio-button'
								defaultChecked={defaultChecked}
								disabled={disabled}
								id={id}
								type='radio'
								value={value}
								{...register ? register(name) : {}}
							/>
						)
						: <input
							checked={checked}
							data-testid='radio-button'
							defaultChecked={defaultChecked}
							disabled={disabled}
							id={id}
							name={name}
							onChange={handleChange}
							type='radio'
						/>
					}
					<span className={cn('f5gRadioButtonChecked', paletteClasses)} />
				</label>
			</Tooltip>
			{!isLabelBefore && (
				<Label
					isFieldLabel={false}
					label={label}
					onClick={handleLabelClick}
				/>
			)}
			{!!infoText
				&& <span
					className='f5gRadioButtonInfoIcon'
					onMouseEnter={toggleIsInfoHovered}
					onMouseLeave={toggleIsInfoHovered}
				>
					<Tooltip content={infoText}>
						{Icons.guidelineIcon({ isActive: isInfoHovered })}
					</Tooltip>
				</span>
			}
		</div>
	);
};

export default memo(RadioButton) as <T extends FieldValues>(props: IRadioButtonProps<T>) => JSX.Element;
